(function () {
  'use strict';

  /*
   * SC BEATS GLOBAL AUDIO ENGINE
   * One audio element controls every playable beat on the storefront.
   * The sticky player mirrors the same engine, so collection rows,
   * featured collections, related beats and product pages stay synchronized.
   */

  var audio = null;
  var activeEl = null;
  var activeUrl = '';
  var waveformCache = Object.create(null);

  function formatDuration(seconds) {
    if (!isFinite(seconds) || seconds < 0) return '--:--';
    seconds = Math.round(seconds);
    return Math.floor(seconds / 60) + ':' + String(seconds % 60).padStart(2, '0');
  }

  function icon(playing) {
    return playing
      ? '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 5h4v14H7zm6 0h4v14h-4z"></path></svg>'
      : '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5.2v13.6c0 .8.9 1.3 1.6.9l10-6.8a1.1 1.1 0 0 0 0-1.8l-10-6.8C8.9 4 8 4.4 8 5.2Z"></path></svg>';
  }

  function ensureAudio() {
    if (audio) return audio;
    audio = document.createElement('audio');
    audio.preload = 'none';
    audio.setAttribute('playsinline', '');
    audio.setAttribute('aria-hidden', 'true');
    audio.style.display = 'none';
    document.body.appendChild(audio);

    audio.addEventListener('timeupdate', syncAll);
    audio.addEventListener('loadedmetadata', function () {
      syncAll();
      updatePlayerMetadata();
      setPlayerDuration(audio.duration);
    });
    audio.addEventListener('play', function () {
      syncButtons(true);
      updatePlayerState();
    });
    audio.addEventListener('pause', function () {
      syncButtons(false);
      updatePlayerState();
    });
    audio.addEventListener('ended', function () {
      audio.currentTime = 0;
      syncButtons(false);
      updatePlayerState();
      syncAll();
    });
    audio.addEventListener('error', function () {
      syncButtons(false);
      updatePlayerState();
    });

    return audio;
  }

  function allPlayButtons() {
    return document.querySelectorAll('[data-beat-play], [data-wave-play]');
  }

  function syncButtons(playing) {
    allPlayButtons().forEach(function (button) {
      var host = button.closest('[data-audio-url]');
      var isCurrent = !!(host && host.dataset.audioUrl === activeUrl);
      var isPlaying = isCurrent && playing && audio && !audio.paused;
      button.innerHTML = icon(isPlaying);
      button.classList.toggle('is-playing', isPlaying);
      button.classList.toggle('is-paused', !isPlaying);
      button.setAttribute('aria-label', isPlaying ? 'Pause beat preview' : 'Play beat preview');
      button.setAttribute('aria-pressed', isPlaying ? 'true' : 'false');
    });
  }

  function placeholderPeaks(url, count) {
    count = count || 180;
    var value = 2166136261;
    var str = String(url || 'waveform');
    for (var j = 0; j < str.length; j++) {
      value ^= str.charCodeAt(j);
      value = Math.imul(value, 16777619);
    }
    var peaks = [];
    for (var i = 0; i < count; i++) {
      value = (Math.imul(value, 1664525) + 1013904223) >>> 0;
      var n = value / 4294967296;
      peaks.push(Math.max(0.08, Math.min(1, 0.22 + n * 0.72)));
    }
    return peaks;
  }

  function draw(canvas, peaks, progress, playedColor, idleColor) {
    if (!canvas) return;
    var rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var width = Math.max(1, Math.round(rect.width * dpr));
    var height = Math.max(1, Math.round(rect.height * dpr));
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }
    var ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, width, height);
    var mid = height / 2;
    var barWidth = width / peaks.length;
    var played = Math.max(0, Math.min(1, progress || 0));
    ctx.fillStyle = idleColor || '#bdbdbd';
    for (var i = 0; i < peaks.length; i++) {
      var amp = Math.max(0.05, peaks[i] || 0);
      var h = Math.max(3 * dpr, amp * height * 0.72);
      ctx.fillStyle = (i / peaks.length) <= played ? (playedColor || '#111111') : (idleColor || '#bdbdbd');
      ctx.fillRect(Math.round(i * barWidth), Math.round(mid - h / 2), Math.max(1, Math.floor(barWidth - 1)), Math.round(h));
    }
  }

  function getPeaks(url) {
    if (!waveformCache[url]) waveformCache[url] = placeholderPeaks(url);
    return waveformCache[url];
  }

  function progress() {
    if (!audio || !audio.duration || !isFinite(audio.duration)) return 0;
    return Math.max(0, Math.min(1, audio.currentTime / audio.duration));
  }

  function syncCanvas(host) {
    if (!host) return;
    var canvas = host.querySelector('canvas');
    if (!canvas) return;
    draw(canvas, getPeaks(host.dataset.audioUrl), host.dataset.audioUrl === activeUrl ? progress() : 0);
    var current = host.querySelector('[data-wave-current], [data-duration-current]');
    var duration = host.querySelector('[data-wave-duration], [data-duration]');
    if (host.dataset.audioUrl === activeUrl && audio) {
      if (current) current.textContent = formatDuration(audio.currentTime);
      if (duration && isFinite(audio.duration)) duration.textContent = formatDuration(audio.duration);
    }
  }

  function syncAll() {
    document.querySelectorAll('[data-audio-url]').forEach(syncCanvas);
    updatePlayerState();
  }

  function playHost(host) {
    var url = host && host.dataset.audioUrl;
    if (!url) return;
    var player = ensureAudio();

    if (activeUrl === url) {
      if (player.paused) {
        player.play().catch(function (error) { console.warn('[SC BEATS] Preview playback failed', error); });
      } else {
        player.pause();
      }
      return;
    }

    if (!player.paused) player.pause();
    activeEl = host;
    activeUrl = url;
    showPlayer();
    player.src = url;
    player.currentTime = 0;
    player.load();

    updatePlayerMetadata();
    setPlayerStatus('Loading…');
    player.play().catch(function (error) {
      console.warn('[SC BEATS] Preview playback failed', error);
      syncButtons(false);
      setPlayerStatus('Play');
    });

    syncButtons(true);
    syncAll();
  }

  function seekHost(host, clientX) {
    var url = host.dataset.audioUrl;
    if (!audio || activeUrl !== url || !isFinite(audio.duration)) return;
    var canvas = host.querySelector('canvas');
    if (!canvas) return;
    var rect = canvas.getBoundingClientRect();
    var ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    audio.currentTime = ratio * audio.duration;
    syncAll();
  }

  function bind(root) {
    root = root || document;

    root.querySelectorAll('[data-beat-play], [data-wave-play]').forEach(function (button) {
      if (button.dataset.scAudioBound === '1') return;
      button.dataset.scAudioBound = '1';
      button.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        playHost(button.closest('[data-audio-url]'));
      });
    });

    root.querySelectorAll('[data-audio-url] canvas').forEach(function (canvas) {
      if (canvas.dataset.scWaveBound === '1') return;
      canvas.dataset.scWaveBound = '1';
      var host = canvas.closest('[data-audio-url]');
      canvas.addEventListener('click', function (event) {
        if (activeUrl !== host.dataset.audioUrl) playHost(host);
        else seekHost(host, event.clientX);
      });
      canvas.addEventListener('keydown', function (event) {
        if (activeUrl !== host.dataset.audioUrl || !audio || !isFinite(audio.duration)) return;
        if (event.key === 'ArrowLeft') { audio.currentTime = Math.max(0, audio.currentTime - 5); event.preventDefault(); }
        if (event.key === 'ArrowRight') { audio.currentTime = Math.min(audio.duration, audio.currentTime + 5); event.preventDefault(); }
        syncAll();
      });
      syncCanvas(host);
    });

    root.querySelectorAll('[data-add-beat]').forEach(function (button) {
      if (button.dataset.scCartBound === '1') return;
      button.dataset.scCartBound = '1';
      button.addEventListener('click', function (event) {
        event.preventDefault(); event.stopPropagation(); addToCart(button);
      });
    });

    syncButtons(audio ? !audio.paused : false);
  }

  function addToCart(button) {
    var variantId = button.dataset.variantId;
    if (!variantId) return;
    button.disabled = true;
    button.classList.add('is-loading');
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    fetch(root + 'cart/add.js', {
      method: 'POST', credentials: 'same-origin',
      headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
      body: JSON.stringify({items: [{id: Number(variantId), quantity: 1}], sections: 'cart-drawer,cart-icon-bubble', sections_url: window.location.pathname})
    }).then(function (response) {
      return response.json().then(function (data) { if (!response.ok) throw new Error(data.description || data.message || 'Unable to add to cart'); return data; });
    }).then(function (state) {
      var drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.renderContents === 'function' && state.sections) drawer.renderContents(state);
      document.dispatchEvent(new CustomEvent('cart:refresh', {detail: state}));
      document.dispatchEvent(new CustomEvent('scbeat:cart-added', {detail: state}));
      button.classList.add('is-added');
      setTimeout(function () { button.classList.remove('is-added'); }, 1000);
    }).catch(function (error) {
      console.error('[SC BEATS] Add to cart failed:', error);
      window.location.href = button.dataset.productUrl || '#';
    }).finally(function () {
      button.disabled = false; button.classList.remove('is-loading');
    });
  }

  function hydrateMissingDurations(root) {
    root = root || document;
    var nodes = Array.prototype.slice.call(root.querySelectorAll('[data-audio-url]'));
    var queue = nodes.filter(function (host) {
      var durationEl = host.querySelector('[data-duration]');
      return durationEl && (!durationEl.textContent.trim() || durationEl.textContent.trim() === '--:--');
    });
    function next() {
      var host = queue.shift(); if (!host) return;
      var url = host.dataset.audioUrl; if (!url) { next(); return; }
      var probe = document.createElement('audio'); probe.preload = 'metadata'; probe.src = url; var done = false;
      var finish = function () {
        if (done) return; done = true;
        var el = host.querySelector('[data-duration]'); if (el && isFinite(probe.duration)) el.textContent = formatDuration(probe.duration);
        probe.removeAttribute('src'); try { probe.load(); } catch (_) {} setTimeout(next, 0);
      };
      probe.addEventListener('loadedmetadata', finish, {once:true}); probe.addEventListener('error', finish, {once:true}); setTimeout(finish, 5000);
    }
    if (queue.length) {
      var start = function () { next(); };
      if ('requestIdleCallback' in window) window.requestIdleCallback(start, {timeout:1800}); else setTimeout(start, 500);
    }
  }

  /* ---------- Sticky player ---------- */
  function playerEl(selector) { return document.querySelector(selector); }
  function setPlayerStatus(text) { var el = playerEl('[data-sc-player-status]'); if (el) el.textContent = text; }
  function setPlayerDuration(seconds) { var el = playerEl('[data-sc-player-duration]'); if (el) el.textContent = formatDuration(seconds); }

  function updatePlayerMetadata() {
    var player = playerEl('[data-sc-global-player]');
    if (!player) return;
    var title = player.querySelector('[data-sc-player-title]');
    var artwork = player.querySelector('[data-sc-player-artwork]');
    var host = activeEl;
    if (!host) return;
    if (title) title.textContent = host.dataset.beatTitle || 'Beat preview';
    if (artwork) {
      var src = host.dataset.artwork;
      if (src) { artwork.src = src; artwork.hidden = false; }
      else { artwork.hidden = true; }
    }
  }

  function updatePlayerState() {
    var player = playerEl('[data-sc-global-player]');
    if (!player || !audio) return;
    var progressEl = player.querySelector('[data-sc-player-progress]');
    var currentEl = player.querySelector('[data-sc-player-current]');
    var durationEl = player.querySelector('[data-sc-player-duration]');
    var play = player.querySelector('[data-sc-player-play]');
    if (progressEl) progressEl.value = audio.duration ? (audio.currentTime / audio.duration) * 100 : 0;
    if (currentEl) currentEl.textContent = formatDuration(audio.currentTime);
    if (durationEl && isFinite(audio.duration)) durationEl.textContent = formatDuration(audio.duration);
    if (play) { play.innerHTML = icon(!audio.paused && activeUrl); play.setAttribute('aria-label', !audio.paused ? 'Pause' : 'Play'); }
  }

  function getQueue() {
    var out = [], seen = Object.create(null);
    document.querySelectorAll('[data-audio-url]').forEach(function (host) {
      var url = host.dataset.audioUrl; if (!url || seen[url]) return; seen[url] = true; out.push(host);
    });
    return out;
  }

  function moveTrack(direction) {
    var queue = getQueue(); if (!queue.length) return;
    var index = queue.findIndex(function (el) { return el.dataset.audioUrl === activeUrl; });
    if (index < 0) index = direction > 0 ? -1 : 0;
    var next = queue[(index + direction + queue.length) % queue.length];
    if (next) playHost(next);
  }

  function bindPlayer() {
    var player = playerEl('[data-sc-global-player]'); if (!player || player.dataset.bound === '1') return;
    player.dataset.bound = '1';
    var play = player.querySelector('[data-sc-player-play]');
    if (play) play.addEventListener('click', function () { if (activeEl) playHost(activeEl); else { var first = getQueue()[0]; if (first) playHost(first); } });
    var prev = player.querySelector('[data-sc-player-prev]'); if (prev) prev.addEventListener('click', function () { moveTrack(-1); });
    var next = player.querySelector('[data-sc-player-next]'); if (next) next.addEventListener('click', function () { moveTrack(1); });
    var progressEl = player.querySelector('[data-sc-player-progress]');
    if (progressEl) progressEl.addEventListener('input', function () { if (audio && isFinite(audio.duration)) { audio.currentTime = (Number(progressEl.value) / 100) * audio.duration; updatePlayerState(); } });
    var close = player.querySelector('[data-sc-player-close]');
    if (close) close.addEventListener('click', function () { if (audio) audio.pause(); player.classList.remove('is-visible'); });
  }

  function showPlayer() { var p = playerEl('[data-sc-global-player]'); if (p) p.classList.add('is-visible'); }

  function init() {
    bindPlayer();
    bind(document);
    hydrateMissingDurations(document);
  }

  window.SCBeatAudioPreview = {
    init: init,
    play: playHost,
    pause: function () { if (audio) audio.pause(); },
    getAudio: function () { return audio; },
    formatDuration: formatDuration,
    getPlaceholderWaveform: function (url) { return getPeaks(url); },
    drawWaveform: draw,
    registerAudio: function () {},
    getActiveUrl: function () { return activeUrl; }
  };

  document.addEventListener('shopify:section:load', function (event) { bindPlayer(); bind(event.target); hydrateMissingDurations(event.target); });
  document.addEventListener('shopify:section:unload', function () { syncAll(); });

  if (window.MutationObserver) {
    var observer = new MutationObserver(function (mutations) {
      var shouldBind = false;
      mutations.forEach(function (mutation) {
        for (var i = 0; i < mutation.addedNodes.length; i++) {
          var node = mutation.addedNodes[i];
          if (node.nodeType === 1 && node.matches && (node.matches('[data-audio-url]') || node.querySelector('[data-audio-url]'))) { shouldBind = true; break; }
        }
      });
      if (shouldBind) { bind(document); hydrateMissingDurations(document); }
    });
    observer.observe(document.body, {childList:true, subtree:true});
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
